import {
    galleriIdFor,
    lagKommunaltBildeBeskrivelse,
    lesKommunaltBilde,
    resolveElementVisning,
    type ElementVisning,
    type SentraltGalleriTilstand,
} from '@origo/galleri';
import type { Seksjonselement } from '@origo/seksjon';
import type { SectionElement } from './types';

export const SENTRALT_LASTER_TITTEL = 'Henter fra DiBK …';
export const SENTRALT_UTILGJENGELIG_TITTEL = 'Illustrasjon fra DiBK – kunne ikke hentes';

/**
 * Lagret seksjonselement → admin-visning. Kommunale bilder bruker lagret tekst;
 * sentrale elementer får alt innhold fra DiBK, og en forklarende tittel mens
 * galleriet laster eller hvis det ikke kan hentes.
 */
export function tilSectionElement(
    el: Seksjonselement,
    galleri: SentraltGalleriTilstand,
    seksjonsGlobalId: string | undefined,
): SectionElement {
    const base = {
        id: el.objectId,
        fil_id: el.filId ?? null,
        galleriElementId: el.galleriElementId ?? null,
        prioritet: el.prioritet,
        SeksjonsGlobalID: seksjonsGlobalId,
    };
    const kommunalt = lesKommunaltBilde(el.beskrivelse);
    if (kommunalt) {
        return {
            ...base,
            title: el.tittel ?? '',
            imageUrl: kommunalt.bildeUrl,
            description: kommunalt.beskrivelse,
            _persistUri: kommunalt.bildeUrl,
        };
    }

    return medVisning(base, resolveElementVisning(el, galleri));
}

/**
 * Slår opp DiBK-innholdet på nytt for et sentralt element i et lokalt utkast,
 * så tittel og status følger galleriet også etter at utkastet ble laget
 * (f.eks. mens galleriet fortsatt lastet). Andre elementer returneres uendret.
 */
export function medSentraltInnhold(el: SectionElement, galleri: SentraltGalleriTilstand): SectionElement {
    if (!el.sentralt) return el;
    return medVisning(el, resolveElementVisning({ filId: el.fil_id, galleriElementId: el.galleriElementId }, galleri));
}

function medVisning<T extends object>(base: T, visning: ElementVisning) {
    const title = visning.status === 'laster'
        ? SENTRALT_LASTER_TITTEL
        : visning.status === 'utilgjengelig'
            ? SENTRALT_UTILGJENGELIG_TITTEL
            : visning.tittel;
    return {
        ...base,
        sentralt: visning.sentralt,
        dibkStatus: visning.status,
        title,
        imageUrl: visning.imageUrl,
        description: visning.beskrivelse ?? '',
    };
}

/**
 * Feltene som lagres for et nytt seksjonselement. Elementer fra sentralt
 * galleri lagres kun som referanse (`GallerielementId`) — tittel, beskrivelse
 * og bilde hentes alltid fra DiBK, så en kopi i AGOL ville bare blitt utdatert.
 * En gammel `FilId` beholdes ved kopiering av eldre rader.
 */
export function lagringsfelter(el: {
    sentralt: boolean;
    kommunalt: boolean;
    filId: string | null;
    galleriElementId?: number | null;
    persistUri?: string | null;
    tittel: string;
    beskrivelse: string | null;
}): { filId?: string; galleriElementId?: number; tittel?: string; beskrivelse?: string } {
    if (el.sentralt) {
        return {
            galleriElementId: galleriIdFor(el) ?? undefined,
            filId: el.filId ?? undefined,
        };
    }
    const beskrivelse = el.persistUri
        ? lagKommunaltBildeBeskrivelse(el.persistUri, el.beskrivelse)
        : el.beskrivelse ?? '';
    return {
        filId: el.kommunalt ? undefined : (el.filId ?? undefined),
        tittel: el.tittel,
        beskrivelse: beskrivelse || undefined,
    };
}
